import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Hash,
  Mic,
  RefreshCw,
  Users,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import {
  Panel,
  PageTitle,
  StarDivider,
} from "@/components/common";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Lobby() {
  const { user } = useAuth();
  const canEdit = user?.role !== "viewer";
  const [tours, setTours] = useState([]);
  const [tid, setTid] = useState("");
  const [tourSearch, setTourSearch] = useState("");
  const [tourSearchOpen, setTourSearchOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [lobbies, setLobbies] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(false);
  const [movingTeamId, setMovingTeamId] = useState("");
  const [swapTeamId, setSwapTeamId] = useState("");
  const [swapping, setSwapping] = useState(false);
  const [
    provisionalSwapTeamId,
    setProvisionalSwapTeamId,
  ] = useState("");
  const [
    provisionalSwapping,
    setProvisionalSwapping,
  ] = useState(false);

  useEffect(() => {
    api.get("/tournaments", {
      params: { templates: false },
    }).then(({ data }) => {
      setTours(data);

      const available = data.filter(
        (tour) =>
          String(tour.state ?? tour.status ?? "").toLowerCase() !== "archived"
      );

      if (available[0]) {
        setTid(available[0].id);
        setTourSearch(available[0].name);
      }
    });
  }, []);

  const filteredTours = useMemo(() => {
    const query = tourSearch.trim().toLowerCase();

    return tours.filter((tour) => {
      const archived =
        String(tour.state ?? tour.status ?? "").toLowerCase() === "archived";
      return (showArchived || !archived) &&
        (!query || String(tour.name || "").toLowerCase().includes(query));
    });
  }, [tours, tourSearch, showArchived]);

  const selectTournament = (tour) => {
    setTid(tour.id);
    setTourSearch(tour.name);
    setTourSearchOpen(false);
    setSwapTeamId("");
    setProvisionalSwapTeamId("");
  };

  const loadLobbyData = async () => {
    if (!tid) return;

    setLoading(true);

    try {
      const [lobbiesResponse, teamsResponse] =
        await Promise.all([
          api.get("/lobbies", {
            params: { tournament_id: tid },
          }),
          api.get("/teams", {
            params: { tournament_id: tid },
          }),
        ]);

      setLobbies(lobbiesResponse.data);

      setTeams(
        Object.fromEntries(
          teamsResponse.data.map((team) => [
            team.id,
            team,
          ])
        )
      );
    } catch (e) {
      toast.error(
        formatError(e.response?.data?.detail)
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLobbyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tid]);

  const moveTeam = async (teamId, targetLobbyId) => {
    if (!teamId || !targetLobbyId) return;

    setMovingTeamId(teamId);

    try {
      await api.post("/lobbies/move-team", {
        team_id: teamId,
        target_lobby_id: targetLobbyId,
      });

      toast.success("Squadra spostata nella nuova lobby");
      await loadLobbyData();
    } catch (e) {
      toast.error(
        formatError(e.response?.data?.detail)
      );
    } finally {
      setMovingTeamId("");
    }
  };

  const moveProvisionalTeam = async (
    teamId,
    targetLobbyNumber
  ) => {
    if (!teamId || !targetLobbyNumber) return;

    setMovingTeamId(teamId);

    try {
      await api.post("/lobbies/provisional/move-team", {
        team_id: teamId,
        target_lobby_number: Number(targetLobbyNumber),
      });

      toast.success(
        "Squadra spostata nella nuova lobby provvisoria"
      );
      await loadLobbyData();
    } catch (e) {
      toast.error(
        formatError(e.response?.data?.detail)
      );
    } finally {
      setMovingTeamId("");
    }
  };

  const swapProvisionalTeams = async (
    teamAId,
    teamBId
  ) => {
    if (!teamAId || !teamBId || teamAId === teamBId) return;

    setProvisionalSwapping(true);

    try {
      await api.post("/lobbies/provisional/swap-teams", {
        team_a_id: teamAId,
        team_b_id: teamBId,
      });

      toast.success(
        "Squadre scambiate tra le lobby provvisorie"
      );
      setProvisionalSwapTeamId("");
      await loadLobbyData();
    } catch (e) {
      toast.error(
        formatError(e.response?.data?.detail)
      );
    } finally {
      setProvisionalSwapping(false);
    }
  };

  const sortedLobbyTeamIds = (lobby) => {
    const slots = lobby.team_slots || {};

    return [...(lobby.team_ids || [])].sort((a, b) => {
      const slotA = Number(slots[a]);
      const slotB = Number(slots[b]);

      const validA = Number.isFinite(slotA) && slotA > 0;
      const validB = Number.isFinite(slotB) && slotB > 0;

      if (validA && validB) return slotA - slotB;
      if (validA) return -1;
      if (validB) return 1;

      return String(teams[a]?.name || a).localeCompare(
        String(teams[b]?.name || b)
      );
    });
  };

  const swapTeams = async (teamAId, teamBId) => {
    if (!teamAId || !teamBId || teamAId === teamBId) return;

    setSwapping(true);

    try {
      await api.post("/lobbies/swap-teams", {
        team_a_id: teamAId,
        team_b_id: teamBId,
      });

      toast.success("Squadre scambiate tra le lobby");
      setSwapTeamId("");
      await loadLobbyData();
    } catch (e) {
      toast.error(
        formatError(e.response?.data?.detail)
      );
    } finally {
      setSwapping(false);
    }
  };

  const provisionalLobbies = useMemo(() => {
    const grouped = {};

    Object.values(teams).forEach((team) => {
      const number = team.provisional_lobby_number;

      if (
        number === null ||
        number === undefined
      ) {
        return;
      }

      if (!grouped[number]) {
        grouped[number] = {
          number,
          name:
            team.provisional_lobby_name ||
            `Lobby ${number}`,
          teams: [],
        };
      }

      grouped[number].teams.push(team);
    });

    return Object.values(grouped)
      .sort((a, b) => a.number - b.number)
      .map((lobby) => {
        const kdValues = lobby.teams
          .map((team) => Number(team.avg_kd))
          .filter((kd) => Number.isFinite(kd));

        const avgKd = kdValues.length
          ? (
              kdValues.reduce(
                (sum, kd) => sum + kd,
                0
              ) / kdValues.length
            ).toFixed(2)
          : "-";

        return {
          ...lobby,
          avg_kd: avgKd,
        };
      });
  }, [teams]);

  const unassignedTeams = useMemo(
    () =>
      Object.values(teams).filter(
        (team) =>
          team.provisional_lobby_number === null ||
          team.provisional_lobby_number ===
            undefined
      ),
    [teams]
  );

  const generate = async () => {
    try {
      const { data } = await api.post(
        `/tournaments/${tid}/generate-lobbies`
      );

      setLobbies(data);

      toast.success(
        `${data.length} lobby generate`
      );

      await loadLobbyData();
    } catch (e) {
      toast.error(
        formatError(e.response?.data?.detail)
      );
    }
  };

  return (
    <div>
      <PageTitle
        title="Lobby"
        subtitle="Simulazione, bilanciamento & Canali Discord"
        action={
          <div className="flex gap-3 flex-wrap">
            <div className="relative min-w-[260px]">
                <input
                  type="text"
                  value={tourSearch}
                  onChange={(e) => {
                    setTourSearch(e.target.value);
                    setTid("");
                    setTourSearchOpen(true);
                  }}
                  onFocus={() => {
                    setTourSearch("");
                    setTourSearchOpen(true);
                  }}
                  onBlur={() =>
                    window.setTimeout(() => setTourSearchOpen(false), 150)
                  }
                  placeholder="Cerca torneo..."
                  autoComplete="off"
                  data-testid="lobby-tour-search"
                  className="w-full bg-[#0a0a0a] border border-[var(--steel)]/50 clip-beveled-sm px-3 py-2 text-white font-rajdhani focus:border-[var(--gold)] focus:outline-none"
                />

                {tourSearchOpen && (
                  <div className="absolute left-0 right-0 top-full mt-1 z-50 max-h-60 overflow-y-auto bg-[#0a0a0a] border border-[var(--steel)]/50 shadow-xl">
                    {filteredTours.length > 0 ? (
                      filteredTours.map((tour) => (
                        <button
                          key={tour.id}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectTournament(tour)}
                          className="block w-full text-left px-3 py-2 text-white font-rajdhani hover:bg-white/10"
                        >
                          {tour.name}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-sm text-gray-400 font-rajdhani">
                        Nessun torneo trovato
                      </div>
                    )}
                  </div>
                )}
                <label className="flex items-center gap-2 mt-2 text-sm text-[var(--silver)] font-rajdhani cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showArchived}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setShowArchived(checked);
                      setTourSearch("");
                      setTourSearchOpen(true);
                      if (!checked) {
                        const selected = tours.find((tour) => tour.id === tid);
                        if (selected &&
                            String(selected.state ?? selected.status ?? "").toLowerCase() === "archived") {
                          const first = tours.find((tour) =>
                            String(tour.state ?? tour.status ?? "").toLowerCase() !== "archived"
                          );
                          setTid(first?.id || "");
                          setTourSearch(first?.name || "");
                        }
                      }
                    }}
                    className="accent-[#D4AF37]"
                  />
                  Mostra tornei archiviati
                </label>
              </div>

            <button
              type="button"
              onClick={loadLobbyData}
              disabled={!tid || loading}
              className="btn-outline-gold clip-beveled-sm px-4 py-2.5 flex items-center gap-2 disabled:opacity-50"
            >
              <RefreshCw
                className={`w-4 h-4 ${
                  loading ? "animate-spin" : ""
                }`}
              />
              Aggiorna
            </button>

            {canEdit && (
            <button
              data-testid="generate-lobbies-btn"
              onClick={generate}
              disabled={!tid}
              className="btn-gold clip-beveled px-6 py-2.5 disabled:opacity-50"
            >
              Genera Lobby
            </button>
            )}
          </div>
        }
      />

      <div className="mb-10">
        <div className="mb-5">
          <h2 className="heading text-3xl gold-text">
            Simulazione Lobby Provvisorie
          </h2>

          <p className="font-rajdhani text-sm text-[var(--silver)] mt-1 max-w-4xl">
            Situazione corrente delle lobby durante
            le iscrizioni. Le assegnazioni possono
            cambiare automaticamente in base a nuove
            iscrizioni, modifiche roster, esclusioni
            e variazioni del K/D.
          </p>
        </div>

        <Panel className="p-4 mb-6">
          <div className="flex gap-3 items-start">
            <AlertTriangle className="w-5 h-5 text-[var(--gold)] shrink-0 mt-0.5" />

            <div className="font-rajdhani text-sm">
              <div className="text-white font-bold uppercase tracking-wide">
                Anteprima — non definitiva
              </div>

              <div className="text-[var(--silver)] mt-1">
                Questa sezione rappresenta le lobby
                provvisorie attualmente assegnate.
                Le lobby definitive vengono create
                soltanto tramite il pulsante
                {" "}
                <strong className="text-[var(--gold)]">
                  Genera Lobby
                </strong>.
              </div>
            </div>
          </div>
        </Panel>

        <div className="grid md:grid-cols-2 gap-6">
          {provisionalLobbies.map((lobby) => (
            <Panel
              key={lobby.number}
              className="p-6"
              testid={`provisional-lobby-${lobby.number}`}
            >
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="font-rajdhani text-[10px] uppercase tracking-[0.2em] text-[var(--silver)]">
                    Provvisoria
                  </div>

                  <h3 className="heading text-3xl gold-text">
                    {lobby.name}
                  </h3>
                </div>

                <div className="font-oswald text-right">
                  <div className="text-2xl font-bold text-white">
                    {lobby.avg_kd}
                  </div>

                  <div className="font-rajdhani text-[10px] text-[var(--silver)] uppercase">
                    KD Medio
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3 font-rajdhani text-xs text-[var(--silver)] mb-4">
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-[var(--gold)]" />
                  {lobby.teams.length} team
                </span>
              </div>

              <StarDivider className="mb-3" />

              <div className="space-y-1">
                {lobby.teams.map((team) => (
                  <div
                    key={team.id}
                    className="flex flex-wrap items-center justify-between gap-2 font-rajdhani text-sm border-b border-[var(--steel)]/20 py-2"
                  >
                    <span className="text-white">
                      {team.name}
                    </span>

                    <div className="flex items-center gap-2">
                      <span className="text-[var(--gold)] font-oswald">
                        KD {team.avg_kd ?? "-"}
                      </span>

                      {canEdit && provisionalLobbies.length > 1 && (
                        <>
                          <select
                            value=""
                            disabled={
                              movingTeamId === team.id ||
                              provisionalSwapping
                            }
                            onChange={(e) => {
                              const target = e.target.value;

                              if (target) {
                                moveProvisionalTeam(
                                  team.id,
                                  target
                                );
                              }
                            }}
                            data-testid={`provisional-move-${team.id}`}
                            className="bg-[#0a0a0a] border border-[var(--steel)]/50 px-2 py-1 text-xs text-white focus:border-[var(--gold)] focus:outline-none disabled:opacity-50"
                          >
                            <option value="">
                              {movingTeamId === team.id
                                ? "Spostamento..."
                                : "Sposta in..."}
                            </option>

                            {provisionalLobbies
                              .filter(
                                (target) =>
                                  target.number !== lobby.number
                              )
                              .map((target) => (
                                <option
                                  key={target.number}
                                  value={target.number}
                                >
                                  {target.name}
                                </option>
                              ))}
                          </select>

                          {!provisionalSwapTeamId && (
                            <button
                              type="button"
                              data-testid={`select-provisional-swap-team-${team.id}`}
                              className="border border-[var(--gold)]/50 text-[var(--gold)] text-xs rounded px-2 py-1 hover:bg-[var(--gold)]/10 disabled:opacity-50"
                              disabled={
                                provisionalSwapping ||
                                movingTeamId === team.id
                              }
                              onClick={() =>
                                setProvisionalSwapTeamId(team.id)
                              }
                            >
                              Scambia
                            </button>
                          )}

                          {provisionalSwapTeamId === team.id && (
                            <button
                              type="button"
                              className="border border-[var(--steel)] text-[var(--silver)] text-xs rounded px-2 py-1"
                              onClick={() =>
                                setProvisionalSwapTeamId("")
                              }
                            >
                              Annulla
                            </button>
                          )}

                          {provisionalSwapTeamId &&
                            provisionalSwapTeamId !== team.id &&
                            teams[provisionalSwapTeamId]
                              ?.provisional_lobby_number !==
                              lobby.number && (
                              <button
                                type="button"
                                data-testid={`confirm-provisional-swap-team-${team.id}`}
                                className="border border-[var(--gold)] bg-[var(--gold)]/10 text-[var(--gold)] text-xs rounded px-2 py-1 font-bold disabled:opacity-50"
                                disabled={provisionalSwapping}
                                onClick={() =>
                                  swapProvisionalTeams(
                                    provisionalSwapTeamId,
                                    team.id
                                  )
                                }
                              >
                                {provisionalSwapping
                                  ? "Scambio..."
                                  : `Scambia con ${
                                      teams[provisionalSwapTeamId]
                                        ?.name ||
                                      provisionalSwapTeamId
                                    }`}
                              </button>
                            )}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          ))}

          {provisionalLobbies.length === 0 && (
            <p className="font-rajdhani text-[var(--silver)] col-span-2 py-6">
              Nessuna lobby provvisoria disponibile
              per questo torneo.
            </p>
          )}
        </div>

        {unassignedTeams.length > 0 && (
          <Panel className="p-5 mt-6">
            <div className="font-rajdhani text-sm">
              <span className="text-[var(--gold)] font-bold">
                Squadre non assegnate:
              </span>{" "}
              <span className="text-[var(--silver)]">
                {unassignedTeams
                  .map((team) => team.name)
                  .join(", ")}
              </span>
            </div>
          </Panel>
        )}
      </div>

      <StarDivider
        label="LOBBY DEFINITIVE"
        className="mb-6"
      />

      <div className="mb-5">
        <h2 className="heading text-3xl gold-text">
          Lobby Definitive
        </h2>

        <p className="font-rajdhani text-sm text-[var(--silver)] mt-1">
          Queste lobby rappresentano l'assegnazione ufficiale.
          Ogni team riceve uno slot univoco da utilizzare
          all'interno della lobby di gioco.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {lobbies.map((lobby) => (
          <Panel
            key={lobby.id}
            className="p-6"
            testid={`lobby-${lobby.id}`}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="heading text-3xl gold-text">
                {lobby.name}
              </h3>

              <div className="font-oswald text-right">
                <div className="text-2xl font-bold text-white">
                  {lobby.avg_kd}
                </div>

                <div className="font-rajdhani text-[10px] text-[var(--silver)] uppercase">
                  KD Medio
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 font-rajdhani text-xs text-[var(--silver)] mb-4">
              <span className="flex items-center gap-1">
                <Hash className="w-3 h-3 text-[var(--gold)]" />
                {lobby.text_channel || "-"}
              </span>

              <span className="flex items-center gap-1">
                <Mic className="w-3 h-3 text-[var(--gold)]" />
                {lobby.voice_channel || "-"}
              </span>

              <span className="flex items-center gap-1">
                <Users className="w-3 h-3 text-[var(--gold)]" />
                {lobby.team_ids.length} team
              </span>
            </div>

            <StarDivider className="mb-3" />

            <div className="space-y-1">
              {sortedLobbyTeamIds(lobby).map((id) => (
                <div
                  key={id}
                  className="flex flex-wrap items-center justify-between gap-2 font-rajdhani text-sm border-b border-[var(--steel)]/20 py-2"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className="min-w-[58px] text-center px-2 py-1 rounded border border-[var(--gold)]/50 text-[var(--gold)] font-oswald font-bold text-xs"
                      data-testid={`team-slot-${id}`}
                    >
                      SLOT {lobby.team_slots?.[id] ?? "-"}
                    </span>

                    <span className="text-white">
                      {teams[id]?.name || id}
                    </span>

                    <span className="text-[var(--gold)] font-oswald">
                      KD {teams[id]?.avg_kd ?? "-"}
                    </span>
                  </div>

                  {canEdit && lobbies.length > 1 && (
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        aria-label={`Sposta ${teams[id]?.name || id} in un'altra lobby`}
                        data-testid={`move-team-${id}`}
                        className="bg-[var(--navy)] border border-[var(--steel)] text-white text-xs rounded px-2 py-1 disabled:opacity-50"
                        value=""
                        disabled={movingTeamId === id || swapping}
                        onChange={(event) => {
                          const targetLobbyId = event.target.value;

                          if (targetLobbyId) {
                            moveTeam(id, targetLobbyId);
                          }
                        }}
                      >
                        <option value="">
                          {movingTeamId === id
                            ? "Spostamento..."
                            : "Sposta team..."}
                        </option>

                        {lobbies
                          .filter(
                            (target) =>
                              target.id !== lobby.id
                          )
                          .map((target) => (
                            <option
                              key={target.id}
                              value={target.id}
                            >
                              {target.name}
                            </option>
                          ))}
                      </select>

                      {!swapTeamId && (
                        <button
                          type="button"
                          data-testid={`select-swap-team-${id}`}
                          className="border border-[var(--gold)]/50 text-[var(--gold)] text-xs rounded px-2 py-1 hover:bg-[var(--gold)]/10 disabled:opacity-50"
                          disabled={swapping || movingTeamId === id}
                          onClick={() => setSwapTeamId(id)}
                        >
                          Scambia
                        </button>
                      )}

                      {swapTeamId === id && (
                        <button
                          type="button"
                          className="border border-[var(--steel)] text-[var(--silver)] text-xs rounded px-2 py-1"
                          onClick={() => setSwapTeamId("")}
                        >
                          Annulla
                        </button>
                      )}

                      {swapTeamId &&
                        swapTeamId !== id &&
                        teams[swapTeamId]?.lobby_id !== lobby.id && (
                          <button
                            type="button"
                            data-testid={`confirm-swap-team-${id}`}
                            className="border border-[var(--gold)] bg-[var(--gold)]/10 text-[var(--gold)] text-xs rounded px-2 py-1 font-bold disabled:opacity-50"
                            disabled={swapping}
                            onClick={() =>
                              swapTeams(swapTeamId, id)
                            }
                          >
                            {swapping
                              ? "Scambio..."
                              : `Scambia con ${
                                  teams[swapTeamId]?.name ||
                                  swapTeamId
                                }`}
                          </button>
                        )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Panel>
        ))}

        {lobbies.length === 0 && (
          <p className="font-rajdhani text-[var(--silver)] col-span-2 py-10">
            Nessuna lobby definitiva generata per
            questo torneo.
          </p>
        )}
      </div>
    </div>
  );
}
